import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import ProjectProgressBar from "../components/ProjectProgressBar";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../context/AuthContext";
import {
  Plus, MapPin, MessageCircle, RotateCcw, ClipboardCheck, FileText, CheckSquare,
  MoreVertical, CheckCircle2, Trash2, PlayCircle, Trophy, Lock, ChevronDown, ChevronRight,
  Hourglass, Rocket,
} from "lucide-react";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(true);
  const { user } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data.projects || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const active = projects.filter((p) => p.status !== "completed");
  const completed = projects.filter((p) => p.status === "completed");

  const handleComplete = async (p) => {
    const ok = await confirm({
      title: "Mark project completed?",
      message: `"${p.name}" will be frozen — no more edits until you reopen it. This is a soft state you can undo any time.`,
      confirmText: "Mark completed",
    });
    if (!ok) return;
    try {
      await api.post(`/projects/${p.project_id}/complete`);
      toast.success("Project marked completed");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleReopen = async (p) => {
    try {
      await api.post(`/projects/${p.project_id}/reopen`);
      toast.success("Project reopened");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (p) => {
    const ok = await confirm({
      title: `Delete "${p.name}" permanently?`,
      message: "This deletes the project and every milestone, task, VO, payment stage, document and message inside it. This cannot be undone.",
      confirmText: "Delete project",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/projects/${p.project_id}`);
      toast.success("Project deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handlePhase = async (p, phase) => {
    try {
      await api.post(`/projects/${p.project_id}/phase`, { phase });
      toast.success(phase === "prep" ? "Moved to Prep Phase" : "Moved to Active");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">All projects</div>
          <h1 className="font-display text-4xl font-bold text-[#2C2A29] mt-2">Projects</h1>
        </div>
        <Link to="/projects/new" data-testid="new-project-btn" className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New project
        </Link>
      </div>

      {loading && <p className="text-[#7A7571]">Loading…</p>}

      {!loading && projects.length === 0 && (
        <div className="card-soft text-center py-16">
          <p className="text-[#7A7571] mb-4">No projects yet.</p>
          <Link to="/projects/new" className="btn-primary inline-flex items-center gap-2"><Plus size={16} />Create your first</Link>
        </div>
      )}

      {/* Active projects */}
      <div className="space-y-4">
        {active.map((p) => (
          <ProjectTile
            key={p.project_id}
            project={p}
            userRole={user?.role}
            onNavigate={() => navigate(`/projects/${p.project_id}`)}
            onComplete={() => handleComplete(p)}
            onReopen={() => handleReopen(p)}
            onDelete={() => handleDelete(p)}
            onPhase={(phase) => handlePhase(p, phase)}
          />
        ))}
      </div>

      {/* Completed projects — collapsible */}
      {completed.length > 0 && (
        <div className="mt-12" data-testid="completed-section">
          <button
            type="button"
            onClick={() => setShowCompleted((v) => !v)}
            data-testid="completed-toggle"
            className="w-full flex items-center gap-3 px-2 py-3 mb-4 border-t border-[#EBE5D9] hover:bg-[#F7F4EC]/40 rounded-lg transition-colors"
          >
            {showCompleted
              ? <ChevronDown size={16} className="text-[#7A7571]" />
              : <ChevronRight size={16} className="text-[#7A7571]" />}
            <span className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">
              Completed
            </span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]">
              {completed.length}
            </span>
            <span className="ml-auto text-[11px] text-[#7A7571]">
              {showCompleted ? "Hide" : "Show"}
            </span>
          </button>
          {showCompleted && (
            <div className="space-y-4">
              {completed.map((p) => (
                <ProjectTile
                  key={p.project_id}
                  project={p}
                  userRole={user?.role}
                  onNavigate={() => navigate(`/projects/${p.project_id}`)}
                  onComplete={() => handleComplete(p)}
                  onReopen={() => handleReopen(p)}
                  onDelete={() => handleDelete(p)}
                  onPhase={(phase) => handlePhase(p, phase)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectTile({ project: p, userRole, onNavigate, onComplete, onReopen, onDelete, onPhase }) {
  const isCompleted = p.status === "completed";
  const isPrep = p.phase === "prep" && !isCompleted;
  const originalValue = Number(p.original_contract_value || 0);
  const approvedVO = Number(p.approved_vo_total || 0);
  const pendingVO = Number(p.pending_vo_total || 0);
  const chargedValue = Number(p.current_project_value || 0);
  const projectCost = Number(p.project_cost || 0);
  const margin = chargedValue - projectCost;
  const marginPct = chargedValue > 0 ? (margin / chargedValue) * 100 : 0;
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div
      data-testid={`project-card-${p.project_id}`}
      onClick={onNavigate}
      className={`card-soft flex flex-col gap-5 hover:-translate-y-0.5 transition-transform cursor-pointer relative ${isCompleted ? "opacity-80" : ""} ${isPrep ? "!bg-[#FFF7EC] !border-[#F5D9A8]" : ""}`}
    >
      {/* 3-dot menu */}
      <ProjectMenu
        project={p}
        userRole={userRole}
        onComplete={onComplete}
        onReopen={onReopen}
        onDelete={onDelete}
        onPhase={onPhase}
      />

      {/* Top row: title + counters (Pending VOs + Open tasks moved up here) */}
      <div className="flex flex-col xl:flex-row xl:items-start gap-4 xl:gap-6 pr-8">
        <div className="min-w-0 xl:flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl font-bold text-[#2C2A29] leading-tight">{p.name}</h3>
            {isCompleted && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]"
                data-testid={`completed-pill-${p.project_id}`}
              >
                <Lock size={10} /> Completed
              </span>
            )}
            {isPrep && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D]"
                data-testid={`prep-pill-${p.project_id}`}
                title="This project is in the Prep Phase — upcoming pipeline, not actively being worked on yet."
              >
                <Hourglass size={10} /> Prep Phase
              </span>
            )}
          </div>
          <div className="text-sm text-[#7A7571] flex items-center gap-1 mt-1">
            <MapPin size={14} /> <span className="truncate">{p.address}</span>
          </div>
          {/* Behavioural counters + workflow counters — hidden for completed projects */}
          {!isCompleted && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <ProjectCounter
                icon={<FileText size={12} />}
                label={`${p.pending_vos || 0} Pending VO${(p.pending_vos || 0) === 1 ? "" : "s"}`}
                cls="bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]"
                testid={`counter-pending-vos-${p.project_id}`}
              />
              <ProjectCounter
                icon={<CheckSquare size={12} />}
                label={`${p.open_tasks || 0} Open task${(p.open_tasks || 0) === 1 ? "" : "s"}`}
                cls="bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]"
                testid={`counter-open-tasks-${p.project_id}`}
              />
              {p.updates_unread > 0 && (
                <ProjectCounter icon={<MessageCircle size={12} />} label={`${p.updates_unread} update${p.updates_unread === 1 ? "" : "s"}`}
                  cls="bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]" testid={`counter-updates-${p.project_id}`} />
              )}
              {p.rework_pending > 0 && (
                <ProjectCounter icon={<RotateCcw size={12} />} label={`${p.rework_pending} rework`}
                  cls="bg-[#FFEDD5] text-[#9A3412] border-[#FDBA74]" testid={`counter-rework-${p.project_id}`} />
              )}
              {p.contractor_completed_pending > 0 && (
                <ProjectCounter icon={<ClipboardCheck size={12} />} label={`${p.contractor_completed_pending} to review`}
                  cls="bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD]" testid={`counter-review-${p.project_id}`} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full">
        <ProjectProgressBar value={p.progress_pct || 0} />
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[#7A7571] mt-1">Progress</div>
      </div>

      {/* Bottom rows: full cost roll-up (Original / Approved VO / Pending VO / Current total / Project cost / Margin) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 pt-4 border-t border-[#EBE5D9]">
        <div>
          <div className="text-xs text-[#7A7571]">Original contract</div>
          <div className="font-display text-lg font-bold text-[#2C2A29]" data-testid={`tile-original-${p.project_id}`}>
            {money(originalValue)}
          </div>
        </div>
        <div>
          <div className="text-xs text-[#7A7571]">Approved VOs</div>
          <div className="font-display text-lg font-bold text-[#0F766E]" data-testid={`tile-approved-vo-${p.project_id}`}>
            {money(approvedVO)}
          </div>
        </div>
        <div>
          <div className="text-xs text-[#7A7571]">Pending VOs</div>
          <div className={`font-display text-lg font-bold ${pendingVO > 0 ? "text-[#92400E]" : "text-[#2C2A29]"}`} data-testid={`tile-pending-vo-${p.project_id}`}>
            {money(pendingVO)}
          </div>
        </div>
        <div>
          <div className="text-xs text-[#7A7571]">Current total</div>
          <div className="font-display text-lg font-bold text-[#2C2A29]" data-testid={`tile-value-${p.project_id}`}>
            {money(chargedValue)}
          </div>
        </div>
        <div>
          <div className="text-xs text-[#7A7571]">Project cost</div>
          <div className="font-display text-lg font-bold text-[#2C2A29]" data-testid={`tile-cost-${p.project_id}`}>
            {money(projectCost)}
          </div>
        </div>
        <div>
          <div className="text-xs text-[#7A7571]">Margin</div>
          <div
            className={`font-display text-lg font-bold ${margin >= 0 ? "text-[#166534]" : "text-[#9F1239]"}`}
            data-testid={`tile-margin-${p.project_id}`}
          >
            {margin >= 0 ? "+" : "−"}${Math.abs(margin).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {chargedValue > 0 && (
              <span className="text-xs font-semibold ml-1">({marginPct.toFixed(1)}%)</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectMenu({ project: p, userRole, onComplete, onReopen, onDelete, onPhase }) {
  const [open, setOpen] = useState(false);
  const isCompleted = p.status === "completed";
  const isPrep = p.phase === "prep";
  const isAdmin = userRole === "admin";

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // Give the click that opened us time to bubble away first
    const id = setTimeout(() => window.addEventListener("click", close, { once: true }), 0);
    return () => { clearTimeout(id); window.removeEventListener("click", close); };
  }, [open]);

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  return (
    <div className="absolute top-4 right-4 z-20" onClick={stop}>
      <button
        type="button"
        data-testid={`project-menu-btn-${p.project_id}`}
        onClick={(e) => { stop(e); setOpen((v) => !v); }}
        className="w-8 h-8 flex items-center justify-center rounded-full text-[#7A7571] hover:bg-[#F7F4EC] hover:text-[#2C2A29] transition-colors"
        aria-label="Project actions"
      >
        <MoreVertical size={18} />
      </button>
      {open && (
        <div
          onClick={stop}
          data-testid={`project-menu-${p.project_id}`}
          className="absolute right-0 top-9 min-w-[200px] bg-white rounded-2xl border border-[#EBE5D9] shadow-xl py-1.5 overflow-hidden"
        >
          {!isCompleted && (
            <MenuItem
              icon={<Trophy size={14} />}
              label="Mark completed"
              testid={`project-menu-complete-${p.project_id}`}
              onClick={(e) => { stop(e); setOpen(false); onComplete(); }}
            />
          )}
          {isCompleted && (
            <MenuItem
              icon={<PlayCircle size={14} />}
              label="Reopen project"
              testid={`project-menu-reopen-${p.project_id}`}
              onClick={(e) => { stop(e); setOpen(false); onReopen(); }}
            />
          )}
          {!isCompleted && !isPrep && (
            <MenuItem
              icon={<Hourglass size={14} />}
              label="Move to Prep Phase"
              testid={`project-menu-prep-${p.project_id}`}
              onClick={(e) => { stop(e); setOpen(false); onPhase("prep"); }}
            />
          )}
          {!isCompleted && isPrep && (
            <MenuItem
              icon={<Rocket size={14} />}
              label="Move to Active"
              testid={`project-menu-active-${p.project_id}`}
              onClick={(e) => { stop(e); setOpen(false); onPhase("active"); }}
            />
          )}
          {isAdmin && (
            <>
              <div className="my-1 border-t border-[#EBE5D9]" />
              <MenuItem
                icon={<Trash2 size={14} />}
                label="Delete project"
                testid={`project-menu-delete-${p.project_id}`}
                onClick={(e) => { stop(e); setOpen(false); onDelete(); }}
                danger
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick, testid, danger }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#F7F4EC] transition-colors ${danger ? "text-[#9F1239] hover:bg-[#FEE2E2]" : "text-[#2C2A29]"}`}
    >
      {icon} {label}
    </button>
  );
}

function ProjectCounter({ icon, label, cls, testid }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${cls}`}
      data-testid={testid}
    >
      {icon} {label}
    </span>
  );
}
